import MultipleSelect from './multiple-select'
import React, { ReactElement, useEffect, useMemo } from 'react'
import useSelector from '@hooks/use-selector'
import { AppDispatch, PromotionalCategoryType } from '@types'
import { getPromotionalCategories } from '@store/actions/promotional-categories'
import { selectPromotionalCategories } from '@store/selectors/promotional-categories'
import { useDispatch } from 'react-redux'

const PromotionalCategoriesMultiSelect = ({
  onChange,
  value,
  emptyValue,
  disabled,
  ...props
}: Props): ReactElement => {
  const dispatch = useDispatch<AppDispatch>()
  const promotionalCategories = useSelector(selectPromotionalCategories)

  const emptyValueObject = {
    id: 0,
    name: emptyValue,
  } as PromotionalCategoryType

  const allPromotionalCategories = useMemo(() => {
    if (emptyValue) {
      return [emptyValueObject, ...promotionalCategories]
    }
    return promotionalCategories
  }, [promotionalCategories, emptyValue])

  useEffect(() => {
    if (!promotionalCategories.length) {
      dispatch(getPromotionalCategories())
    }
  }, [])

  const handleOnChange = (options: PromotionalCategoryType[]) => {
    if (
      emptyValue &&
      options.filter((value) => value.id === 0).length && // empty value selected
      value.length // other stuff has been selected
    ) {
      // we want to clear the options if the empty value was selected just now
      onChange([])
    } else {
      onChange(options.filter((value) => value.id !== 0))
    }
  }

  if (value.length && !promotionalCategories.length) return <></>

  return (
    <MultipleSelect
      disabled={disabled}
      onChange={handleOnChange}
      optionKey='id'
      options={allPromotionalCategories}
      optionTitleKey='name'
      optionValueKey='id'
      selectedOptions={!value.length && emptyValue ? [emptyValueObject] : value}
      title='Promotional Categories'
      {...props}
    />
  )
}

export default PromotionalCategoriesMultiSelect

type Props = {
  onChange: (options: PromotionalCategoryType[]) => void
  value: PromotionalCategoryType[]
  emptyValue?: string
  disabled?: boolean
}
